import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  Alert,
  SafeAreaView,
  ScrollView,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { createThemedStyles } from '../theme/useThemedStyles';
import WebAuthLayout from '../components/web/WebAuthLayout';
import { isWeb } from '../utils/platformUtils';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { useTheme } from '../context/ThemeContext';
import { getStyleWithError } from '../utils/styles';

interface SignupScreenProps {
  onBack: () => void;
}

const validateEmail = (email: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const validateName = (name: string) => {
  return name.trim().length >= 2;
};

const SignupScreen = ({ onBack }: SignupScreenProps) => {
  const { signup } = useAuth();
  const { resetSettings } = useSettings();
  const { colors } = useTheme();
  const styles = useSignupStyles();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const validateForm = () => {
    const errors = {
      fullName: '',
      email: '',
      password: '',
      confirmPassword: ''
    };

    if (!fullName.trim()) {
      errors.fullName = 'Full name is required';
    } else if (!validateName(fullName)) {
      errors.fullName = 'Please enter a valid name (at least 2 characters)';
    }

    if (!email.trim()) {
      errors.email = 'Email is required';
    } else if (!validateEmail(email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!password.trim()) {
      errors.password = 'Password is required';
    } else if (password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    if (!confirmPassword.trim()) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setValidationErrors(errors);
    return !errors.fullName && !errors.email && !errors.password && !errors.confirmPassword;
  };

  const handleSignup = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setIsLoading(true);
      await signup(email, password, fullName);
      // Reset settings to default for new user (appLock: false, etc)
      await resetSettings();
      setSuccessMessage('Account created successfully!');
      onBack();
    } catch (error) {
      Alert.alert('Error', 'Failed to create account. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const formFields = (
    <>
            <View style={styles.inputContainer}>
              <Text style={styles.inputLabel}>Full Name</Text>
              <TextInput
                style={getStyleWithError(styles.input, validationErrors.fullName, styles.inputError)}
                placeholder="Full Name"
                value={fullName}
                onChangeText={setFullName}
                autoCapitalize="words"
                autoCorrect={false}
              />
              {validationErrors.fullName ? (
                <Text style={styles.errorText}>{validationErrors.fullName}</Text>
              ) : fullName.length > 0 && validateName(fullName) ? (
                <View style={styles.checkIcon}>
                  <Ionicons name="checkmark-circle" size={24} color={colors.primary.main} />
                </View>
              ) : null}
            </View>

            <View style={styles.inputContainer}>
              <Text style={styles.inputLabel}>Email</Text>
              <TextInput
                style={getStyleWithError(styles.input, validationErrors.email, styles.inputError)}
                placeholder="Email"
                value={email}
                onChangeText={setEmail}
                keyboardType="email-address"
                autoCapitalize="none"
                autoCorrect={false}
              />
              {validationErrors.email ? (
                <Text style={styles.errorText}>{validationErrors.email}</Text>
              ) : email.length > 0 && validateEmail(email) ? (
                <View style={styles.checkIcon}>
                  <Ionicons name="checkmark-circle" size={24} color={colors.primary.main} />
                </View>
              ) : null}
            </View>

            <View style={styles.inputContainer}>
              <Text style={styles.inputLabel}>Password</Text>
              <View style={styles.passwordContainer}>
                <TextInput
                  style={getStyleWithError([styles.input, { paddingRight: 50 }], validationErrors.password, styles.inputError)}
                  placeholder="Password"
                  value={password}
                  onChangeText={setPassword}
                  secureTextEntry={!showPassword}
                  autoCapitalize="none"
                  autoCorrect={false}
                />
                <TouchableOpacity
                  onPress={() => setShowPassword(!showPassword)}
                  style={styles.eyeIcon}
                >
                  <Ionicons
                    name={!showPassword ? 'eye-off-outline' : 'eye-outline'}
                    size={24}
                    color={colors.grey[400]}
                  />
                </TouchableOpacity>
              </View>
              {validationErrors.password ? (
                <Text style={styles.errorText}>{validationErrors.password}</Text>
              ) : null}
            </View>

            <View style={styles.inputContainer}>
              <Text style={styles.inputLabel}>Confirm Password</Text>
              <View style={styles.passwordContainer}>
                <TextInput
                  style={getStyleWithError([styles.input, { paddingRight: 50 }], validationErrors.confirmPassword, styles.inputError)}
                  placeholder="Confirm Password"
                  value={confirmPassword}
                  onChangeText={setConfirmPassword}
                  secureTextEntry={!showConfirmPassword}
                  autoCapitalize="none"
                  autoCorrect={false}
                />
                <TouchableOpacity
                  onPress={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={styles.eyeIcon}
                >
                  <Ionicons
                    name={!showConfirmPassword ? 'eye-off-outline' : 'eye-outline'}
                    size={24}
                    color={colors.grey[400]}
                  />
                </TouchableOpacity>
              </View>
              {validationErrors.confirmPassword ? (
                <Text style={styles.errorText}>{validationErrors.confirmPassword}</Text>
              ) : null}
            </View>

            <TouchableOpacity
              style={[styles.signUpButton, isLoading && styles.buttonDisabled]}
              onPress={handleSignup}
              disabled={isLoading}
            >
              {isLoading ? (
                <ActivityIndicator color="white" size="small" />
              ) : (
                <Text style={styles.signUpButtonText}>SIGN UP</Text>
              )}
            </TouchableOpacity>

            <View style={styles.loginContainer}>
              <Text style={styles.loginText}>Already have an account? </Text>
              <TouchableOpacity onPress={onBack}>
                <Text style={styles.loginLink}>Sign In</Text>
              </TouchableOpacity>
            </View>
          {successMessage ? <Text style={styles.successText}>{successMessage}</Text> : null}
    </>
  );

  if (isWeb) {
    return (
      <WebAuthLayout
        cardTitle="Create account"
        cardSubtitle="Start tracking your financial goals"
        onBack={onBack}
        features={[
          'Free to get started',
          'Sync goals across devices',
          'Upgrade anytime for more goals',
        ]}
      >
        {formFields}
      </WebAuthLayout>
    );
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <LinearGradient
        colors={[colors.auth.panel, colors.primary.dark]}
        style={StyleSheet.absoluteFill}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
      />
      <ScrollView 
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.content}>
          <Text style={styles.headerText}>Create Your</Text>
          <Text style={styles.subHeaderText}>Account</Text>
          <View style={styles.formContainer}>
            {formFields}
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const useSignupStyles = createThemedStyles((colors) => ({
  safeArea: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  content: {
    flex: 1,
    padding: 24,
  },
  headerText: {
    fontSize: 32,
    fontWeight: 'bold',
    color: 'white',
    marginBottom: 8,
  },
  subHeaderText: {
    fontSize: 24,
    color: 'white',
    marginBottom: 32,
  },
  formContainer: {
    backgroundColor: colors.surface,
    borderRadius: 24,
    padding: 24,
    width: '100%',
  },
  inputContainer: {
    marginBottom: 24,
  },
  inputLabel: {
    fontSize: 16,
    color: colors.primary.main,
    marginBottom: 8,
  },
  input: {
    fontSize: 16,
    color: colors.text.primary,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
    paddingVertical: 8,
    paddingHorizontal: 0,
  },
  checkIcon: {
    position: 'absolute',
    right: 0,
    bottom: 8,
  },
  passwordContainer: {
    position: 'relative',
  },
  eyeIcon: {
    position: 'absolute',
    right: 0,
    bottom: 8,
  },
  signUpButton: {
    backgroundColor: colors.loading.background,
    borderRadius: 12,
    height: 56,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 24,
  },
  buttonDisabled: {
    backgroundColor: colors.grey[300],
  },
  signUpButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
  },
  loginContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  loginText: {
    color: colors.text.secondary,
    fontSize: 14,
  },
  loginLink: {
    color: colors.primary.main,
    fontSize: 14,
    fontWeight: 'bold',
  },
  inputError: {
    borderBottomColor: colors.error.main,
  },
  errorText: {
    color: colors.error.main,
    fontSize: 12,
    marginTop: 4,
  },
  successText: {
    color: colors.success.main,
    fontSize: 12,
    marginTop: 4,
  },
}));

export default SignupScreen; 